import { Fragment, useState } from "react";
import FilePreview from "files/components/FilePreview";
import useFileAccess from "files/hooks/useFileAccess";
import { resolveObjectKey, useFilesInfinite } from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileInfo from "files/types/interfaces/fileInfo";
import { FolderPlus, Upload } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import { FileListingHeader } from "~/components/ui/file-listing-header";
import { FileListingBackRow, FileListingRow } from "~/components/ui/file-listing-row";
import { Skeleton } from "~/components/ui/skeleton";
import useUser from "~/contexts/user";
import useUserPermission from "~/hooks/useUserPermission";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { CreateFolderDialog } from "~/pages/manifold-nextgen/components/files/CreateFolderDialog";
import { UploadFileDialog } from "~/pages/manifold-nextgen/components/files/UploadFileDialog";
import { EmbeddedFileJobs } from "~/pages/manifold-nextgen/components/projects/files/EmbeddedFileJobs";

// Lists the caller's personal S3 scratch prefix (the same one the Snowflake
// personal dataset provisions alongside its schema) via the file browser's
// own infrastructure, tracking the browsed folder as local state rather than
// the URL. Rows share FileListingRow/FileListingBackRow with DatasetFiles.tsx
// with actions enabled, since write access is granted per-key (the caller
// already owns their own scratch prefix) rather than scoped to a dataset.
export const PersonalDatasetFiles = () => {
  const user = useUser();
  const rootPath = resolveObjectKey(BaseDirectory.PERSONAL, user?.email ?? "");
  const [currentPath, setCurrentPath] = useState(rootPath);
  const [previewFile, setPreviewFile] = useState<Pick<FileInfo, "key" | "size"> | null>(null);
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const canPreviewFile = useUserPermission("create:file-browser-download");
  const canWrite = useFileAccess(currentPath, "write");
  // Normalized once and reused for both the listing and the write-action dialogs/rows below,
  // since the root path (unlike a directory's own key) has no trailing slash — a mismatch here
  // means the SWR cache key mutate() targets doesn't match the key the listing actually uses,
  // silently no-op'ing the post-write refresh.
  const currentPrefix = currentPath.endsWith("/") ? currentPath : `${currentPath}/`;

  const { allChildren, isLoading, error } = useFilesInfinite(false, currentPrefix);

  const segments =
    currentPath === rootPath ? [] : currentPath.slice(rootPath.length).split("/").filter(Boolean);

  const handleBack = () => setCurrentPath((path) => path.replace(/\/[^/]+\/?$/, ""));

  const writeActions = canWrite && (
    <div className="flex shrink-0 items-center gap-1">
      <EmbeddedFileJobs prefix={currentPrefix} />
      <Button
        variant="ghost"
        size="sm"
        className="text-primary hover:text-primary/80"
        onClick={() => setNewFolderDialogOpen(true)}
      >
        <FolderPlus />
        New Folder
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="text-primary hover:text-primary/80"
        onClick={() => setUploadDialogOpen(true)}
      >
        <Upload />
        Upload Files
      </Button>
    </div>
  );

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
      {segments.length === 0 ? (
        <TabHeader title="Files" action={writeActions} />
      ) : (
        <div className="flex items-start justify-between gap-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <button type="button" onClick={() => setCurrentPath(rootPath)}>
                    All Files
                  </button>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {segments.map((segment, index) => {
                const isLast = index === segments.length - 1;
                const path = `${rootPath}/${segments.slice(0, index + 1).join("/")}/`;
                return (
                  <Fragment key={path}>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      {isLast ? (
                        <BreadcrumbPage>{segment}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <button type="button" onClick={() => setCurrentPath(path)}>
                            {segment}
                          </button>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
          {writeActions}
        </div>
      )}

      <CreateFolderDialog
        open={newFolderDialogOpen}
        onClose={() => setNewFolderDialogOpen(false)}
        prefix={currentPrefix}
      />
      <UploadFileDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        uploadPrefix={currentPrefix}
      />

      <div className="flex flex-col">
        <FileListingHeader />
        {isLoading ? (
          <div role="status" aria-label="Loading files" className="flex flex-col gap-1 py-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-destructive">Unable to load files.</p>
        ) : (
          <>
            {segments.length > 0 && <FileListingBackRow onClick={handleBack} />}
            {allChildren.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                This folder is empty.
              </p>
            ) : (
              allChildren.map((node) => (
                <FileListingRow
                  key={node.key}
                  node={node}
                  navigation="callback"
                  disabled={!canPreviewFile}
                  onNavigate={() => setCurrentPath(node.key)}
                  onPreview={() => setPreviewFile({ key: node.key, size: node.size })}
                  mutatePrefix={currentPrefix}
                />
              ))
            )}
          </>
        )}
      </div>

      {previewFile && <FilePreview file={previewFile} onClose={() => setPreviewFile(null)} />}
    </div>
  );
};
